import React, { useState, useRef } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Message } from '@/types';
import { formatRelativeTime } from '@/lib/utils';
import { Code, Copy, CheckCheck, RefreshCw, Bot, User, Lock } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm'; // Import remarkGfm
import { Button } from '@/components/ui/button';
import CodeHighlighter from '@/components/code/SyntaxHighlighter';
import LinkPreview from '@/components/common/LinkPreview';
import TypingIndicator from './TypingIndicator';
import { cleanMarkdownBold } from '@/lib/utils'; // Import the new utility
import { useKnowledgeBases } from '@/hooks/useKnowledgeBases';
import { useProfile } from '@/hooks/useProfile'; // Import useProfile
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

function extractCodeBlock(content: string): { code: string; language: string } | null {
  const codeBlockRegex = /```([a-zA-Z0-9]*)\n([\s\S]*?)```/m;
  const match = content.match(codeBlockRegex);
  if (match) {
    return {
      language: match[1] || 'plaintext',
      code: match[2] || ''
    };
  }
  return null;
}

function stripMarkdownCodeFencing(raw: string): string {
  const codeBlockRegex = /^```(?:\w+)?\n([\s\S]*?)\n```$/;
  const match = raw.match(codeBlockRegex);
  return match ? match[1] : raw;
}

interface SimpleChatMessageProps {
  message: Message;
  hideCodeBlockIfAttachment?: boolean;
  onRegenerate?: (messageId: string) => void;
}

const SimpleChatMessage: React.FC<SimpleChatMessageProps> = React.memo(({ message, hideCodeBlockIfAttachment, onRegenerate }) => {
  const isAi = message.role === 'assistant';
  const [copied, setCopied] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const { knowledgeBases } = useKnowledgeBases();
  const { profile } = useProfile(); // Get user profile
  const { toast } = useToast();

  const getDisplayContent = (content: string): string => {
    const kbMatch = content.match(/^\/kb\s+([^\s]+)(\s+.+)?/);
    if (kbMatch) {
      const kbId = kbMatch[1];
      const rest = kbMatch[2] || '';
      const matched = knowledgeBases.find(kb => kb.id === kbId);
      if (matched) {
        return `/kb ${matched.name}${rest}`;
      }
    }
    return content;
  };

  const handleCopy = () => {
    if (contentRef.current) {
      const html = contentRef.current.innerHTML;
      const plainText = contentRef.current.innerText;

      try {
        const blobHtml = new Blob([html], { type: 'text/html' });
        const blobText = new Blob([plainText], { type: 'text/plain' });
        const clipboardItem = new ClipboardItem({
          'text/html': blobHtml,
          'text/plain': blobText,
        });

        navigator.clipboard.write([clipboardItem]).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        });
      } catch (error) {
        console.error("Failed to copy rich text, falling back to plain text.", error);
        navigator.clipboard.writeText(plainText).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        });
      }
    }
  };

  // NEW: Seal handler
  const handleSeal = async () => {
    if (!message?.content || message.content.trim() === '...') return;

    // Optional attestation step controlled by Settings
    const attestationEnabled = localStorage.getItem('trust_attestation_enabled') === 'true';
    if (attestationEnabled) {
      const ok = window.confirm('Do you attest this output is trusted and want to seal it?');
      if (!ok) {
        toast({ title: 'Sealing cancelled', description: 'No changes made.' });
        return;
      }
    }

    const ts = new Date(message.timestamp || Date.now()).toISOString();
    const model = (message as any)?.model || 'unknown';
    const md = [
      '# EHCO Sealed Reflection',
      '',
      `- Role: ${message.role}`,
      `- Model: ${model}`,
      `- Timestamp: ${ts}`,
      '',
      '## Content',
      typeof message.content === 'string' ? message.content : JSON.stringify(message.content, null, 2),
      '',
      '---',
      'Sealed locally. If signed in, it is also saved to your private EHCO memory.',
    ].join('\n');

    // Local Markdown download
    const nameSafe = (model || 'reflection').toString().replace(/[^\w.-]+/g, '_');
    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `EHCO_${nameSafe}_${ts}.md`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);

    // Persist to Supabase if signed in
    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData?.session?.user?.id;

    if (!userId) {
      toast({
        title: 'Sealed locally',
        description: 'Sign in to persist sealed items to your private EHCO memory.',
      });
      return;
    }

    const trustEnabled = localStorage.getItem('trust_mode_enabled') === 'true';
    const meta = {
      role: message.role,
      model,
      timestamp: ts,
      trust_enabled: trustEnabled,
      chat_id: (message as any)?.chatId || null,
      commands: (message as any)?.commands || null,
      knowledge_base_id: (message as any)?.knowledgeBaseId || null,
    };

    const { error } = await supabase
      .from('sealed_items')
      .insert({
        user_id: userId,
        chat_id: (message as any)?.chatId || null,
        message_id: message.id,
        content: typeof message.content === 'string' ? message.content : JSON.stringify(message.content),
        meta,
      });

    if (error) {
      console.error('[Seal] Failed to persist sealed item:', error);
      toast({
        title: 'Local seal saved',
        description: 'Could not sync to EHCO memory. Please try again later.',
        variant: 'destructive',
      });
      return;
    }

    toast({
      title: 'Sealed',
      description: 'Saved to your EHCO memory (and downloaded locally).',
    });
  };

  // Attachments
  const codeAttachments = message.attachments?.filter(att => att.type === 'code') || [];
  const imageAttachments = message.attachments?.filter(att => att.type === 'image') || [];
  const canvasAttachments = message.attachments?.filter(att => att.type === 'canvas') || [];
  const fileAttachments = message.attachments?.filter(att => att.type === 'file') || [];

  // If no code attachment, but message content contains a code block, extract and render it
  const codeBlock = (!codeAttachments.length && message.content)
    ? extractCodeBlock(message.content)
    : null;

  const isTyping = isAi && (!message.content || message.content.trim() === '...');

    if (message.content && message.content.trim() !== "...") {
    message.content = getDisplayContent(message.content);
  }

  return (
    <div 
      className={`py-4 px-2 ${isAi ? 'bg-muted/30' : ''}`} 
      data-testid="chat-message" 
      data-message-id={message.id}
    >
      <div className="flex gap-3 justify-start max-w-[95%] md:max-w-[85%] mx-0">
        <Avatar className="h-8 w-8 flex-shrink-0">
          {isAi ? (
            <AvatarFallback>
              <Bot className="h-full w-full p-1 text-primary" />
            </AvatarFallback>
          ) : (
            profile?.avatar ? (
              <AvatarImage src={profile.avatar} />
            ) : (
              <AvatarFallback>
                <User className="h-full w-full p-1 text-muted-foreground" />
              </AvatarFallback>
            )
          )}
        </Avatar>
        
        <div className="flex-1 space-y-2 text-left">
          <div className="flex items-center gap-2">
            <span className="font-semibold">{isAi ? 'AI Assistant' : 'You'}</span>
            <span className="text-xs text-muted-foreground">
              {message.timestamp ? formatRelativeTime(message.timestamp) : "unknown"}
            </span>
            {isAi && message.content && message.content.trim() !== '...' && (
              <div className="ml-auto flex items-center">
                {onRegenerate && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => onRegenerate(message.id)}
                    title="Regenerate response"
                  >
                    <RefreshCw size={14} />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={handleSeal}
                  title="Seal to memory"
                >
                  <Lock size={14} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7" 
                  onClick={handleCopy}
                  title="Copy message"
                >
                  {copied ? (
                    <CheckCheck size={14} className="text-white" />
                  ) : (
                    <Copy size={14} />
                  )}
                </Button>
              </div>
            )}
          </div>
          
          <div ref={contentRef}>
            {isTyping ? (
              <TypingIndicator />
            ) : (
              <>
                {codeAttachments.length > 0 ? (
                  codeAttachments.map((attachment) => {
                    const language = attachment.name?.split('.').pop() || 'plaintext';
                    return (
                      <div key={attachment.id} className="mb-3">
                        <div className="flex items-center gap-2 mb-1">
                          <Code size={16} />
                          <span className="font-medium">{attachment.name || 'Code snippet'}</span>
                        </div>
                        <CodeHighlighter code={stripMarkdownCodeFencing(attachment.content)} language={language} />
                      </div>
                    );
                  })
                ) : codeBlock && !hideCodeBlockIfAttachment ? (
                  <div className="mb-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Code size={16} />
                      <span className="font-medium">{codeBlock.language || 'Code snippet'}</span>
                    </div>
                    <CodeHighlighter code={stripMarkdownCodeFencing(codeBlock.code)} language={codeBlock.language} />
                  </div>
                ) : (
                  message.content && (
                    <div className="text-sm text-foreground/90 whitespace-pre-wrap break-words markdown-content">
                      <ReactMarkdown
                        components={{
                          a: ({ node, ...props }) => <LinkPreview url={props.href || ''} />,
                          p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
                        }}
                        remarkPlugins={[remarkGfm]}
                      >
                        {cleanMarkdownBold(message.content)}
                      </ReactMarkdown>
                    </div>
                  )
                )}

                {imageAttachments.length > 0 && (
                  <div className="space-y-3 mt-2">
                    {imageAttachments.map((attachment) => (
                      <div key={attachment.id} className="overflow-hidden rounded-md border">
                        <img 
                          src={attachment.content} 
                          alt={attachment.name || "Attached image"} 
                          className="max-h-96 w-full object-contain"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
                {canvasAttachments.length > 0 && (
                  <div className="space-y-3 mt-2">
                    {canvasAttachments.map((attachment) => (
                      <div key={attachment.id} className="overflow-hidden rounded-md border">
                        <img 
                          src={attachment.content} 
                          alt="Drawing" 
                          className="max-h-96 w-full object-contain"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
                {fileAttachments.length > 0 && (
                  fileAttachments.map((attachment) => (
                    <div key={attachment.id} className="flex items-center gap-2 rounded-md border p-3">
                      <span className="font-medium">{attachment.name || 'File'}</span>
                      <div className="text-xs text-muted-foreground">
                        {attachment.content.length > 100 
                          ? `${attachment.content.substring(0, 100)}...` 
                          : attachment.content}
                      </div>
                    </div>
                  ))
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});
SimpleChatMessage.displayName = 'SimpleChatMessage';

export default SimpleChatMessage;